/**
 * The Analyze window: point the app at a project's folder and it maps the code into the
 * graph (modules, libraries, classes, interfaces and their links), keeping what you drew by hand.
 *
 * - Type a folder (or Browse… in the desktop app) and Scan; in the browser build, Choose
 *   folder… (read by the page). One Ctrl+Z takes a scan back. See `FolderSection.tsx`.
 * - Or give a repository's address (Git plugin): fetched with this computer's own logins or an
 *   access token, then scanned the same way. See `RepoSection.tsx`.
 * - The report lists what was added and updated; click a node to see it on the graph.
 * - Nodes found by an earlier scan but gone from the code are listed, never removed by
 *   themselves: "Remove" (click twice) does it in one edit.
 * - Which plugins read the code: "Manage plugins" opens the Plugins window.
 * - Then the AI, when one is set up, enriches the scan ("Enrich with AI"): descriptions,
 *   groups, categories, kinds, missing links, and nodes for the code no plugin reads (listed
 *   here). Its suggestions are reviewed in the AI window before anything changes.
 */
import { For, Show, type JSX } from "solid-js";
import type { Node } from "@protocol/Node";
import { useApp } from "../../app/context";
import { ArmedButton, Group, NodeRef } from "../../shared/ui";
import { FolderSection } from "./FolderSection";
import { RepoSection } from "./RepoSection";

/** How many nodes a report list shows before "… and N more". */
const SHOWN = 30;

/** The Analyze window. */
export function AnalyzeWindow(): JSX.Element {
  const { analysis, graph, view, ai, wm } = useApp();
  /** Asks the AI to enrich the scan, showing its suggestions in the AI window. */
  const enrich = () => {
    wm.open("ai");
    if (ai.ready()) void ai.run("enrich");
  };

  /** The graph's nodes with these ids (those still there), at most {@link SHOWN}. */
  const nodes = (ids: string[]): Node[] => {
    const byId = new Map(graph.state.graph.nodes.map((n) => [n.id, n]));
    return ids.slice(0, SHOWN).flatMap((id) => byId.get(id) ?? []);
  };
  /** Selects a node and centres the graph on it. */
  const open = (id: string) => {
    view.selectNode(id);
    view.requestCenter(id);
  };
  /** One report list. */
  const list = (title: string, ids: string[], id: string) => (
    <Group title={title} count={ids.length}>
      <For each={nodes(ids)}>
        {(node) => (
          <NodeRef
            node={node}
            extra={node.source ?? undefined}
            onOpen={() => open(node.id)}
          />
        )}
      </For>
      <Show when={ids.length > SHOWN}>
        <li class="small muted" data-more={id}>
          … and {ids.length - SHOWN} more
        </li>
      </Show>
    </Group>
  );

  return (
    <div class="analyzewin">
      <FolderSection />
      <RepoSection />

      <Show when={analysis.report()}>
        {(r) => (
          <>
            <section class="grp">
              <h3>Last scan</h3>
              <p class="mono small">{r().root}</p>
              <p class="small" data-analysis-summary>
                {r().files} files · {r().analyzers.join(", ")} ·{" "}
                {r().added.length} added, {r().updated.length} updated,{" "}
                {r().unchanged} unchanged · {r().links_added} links
              </p>
              <Show when={r().unread.length}>
                <p class="small muted" id="analysis-unread">
                  No plugin reads {r().unread.map((u) => `${u.files} ${u.language} file${u.files === 1 ? "" : "s"}`).join(", ")}: the AI can read them.
                </p>
              </Show>
              <div class="btnrow">
                <button type="button" class="btn" id="analyze-enrich" disabled={!!ai.busy()} onClick={enrich} title={ai.ready() ? "Descriptions, groups, categories, kinds, missing links, and nodes for unread code: reviewed before anything changes" : "Choose an AI service first"}>
                  {ai.busy() === "enrich" ? "Enriching…" : ai.ready() ? "Enrich with AI" : "Set up an AI to enrich it"}
                </button>
              </div>
            </section>
            <Show when={analysis.vanished().length}>
              <section class="grp">
                <h3>
                  No longer in the code{" "}
                  <span>{analysis.vanished().length}</span>
                </h3>
                <p class="small muted">
                  Deleted or renamed since the last scan. They stay until you
                  remove them.
                </p>
                <ul>
                  <For each={nodes(analysis.vanished())}>
                    {(node) => (
                      <NodeRef
                        node={node}
                        extra={node.source ?? undefined}
                        onOpen={() => open(node.id)}
                      />
                    )}
                  </For>
                </ul>
                <div class="btnrow">
                  <ArmedButton
                    id="analyze-remove-vanished"
                    label={`Remove ${analysis.vanished().length}`}
                    armedLabel="Click again to remove"
                    onConfirm={() => void analysis.removeVanished()}
                  />
                </div>
              </section>
            </Show>
            {list("Added", r().added, "added")}
            {list("Updated", r().updated, "updated")}
            <Group title="Warnings" count={r().warnings.length}>
              <For each={r().warnings}>
                {(w) => <li class="small warn">{w}</li>}
              </For>
            </Group>
          </>
        )}
      </Show>
    </div>
  );
}
